// ./src/components/features/WorkflowItemTable/WorkflowItemTable.tsx
import React, { useCallback } from "react";
import { Text, Group, Stack, Badge, Box, Paper } from "@mantine/core";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  RecordActions,
  StatusBadge,
  AppIcon,
  LoadingIndicator,
} from "@/components/generic";
import { WorkflowItem } from "@/utils/api";

// ---------------------------------------------------------------------------
// Label maps
//
// Keys match the uppercase values stored in the DB
// (ALWAYS, ON_FIELD_EMPTY, NOTIFY, REJECT, …).
// ---------------------------------------------------------------------------

const CONDITION_TYPE_LABELS: Record<string, string> = {
  ALWAYS: "Always",
  ON_FIELD_EMPTY: "Field is empty",
  ON_FIELD_NOT_EMPTY: "Field is not empty",
  ON_FIELD_VALUE_COMPARE: "Field value comparison",
  CUSTOM: "Custom expression",
};

const ACTION_TYPE_LABELS: Record<string, string> = {
  NOTIFY: "Notify (connector / email)",
  REJECT: "Reject submission",
  TRANSFORM: "Transform data",
};

const labelFor = (
  map: Record<string, string>,
  key: string | undefined,
): string => (key ? (map[key] ?? key) : "—");

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface WorkflowItemTableProps {
  /** Workflow items to display — should already be sorted by orderNo */
  workflowItems: WorkflowItem[];

  /** True while the initial page is loading */
  loading: boolean;

  /** Callback when the Edit action is clicked */
  onEditWorkflowItem?: (workflowItem: WorkflowItem) => void;

  /** Callback when the Remove action is clicked */
  onRemoveWorkflowItem?: (workflowItem: WorkflowItem) => void;

  /**
   * Set of IDs currently being deleted.
   * Each id causes its row's remove button to show a spinner.
   */
  removingIds?: Set<string>;

  /**
   * ID of the workflow item most recently created or updated.
   * That row is highlighted until the list is reloaded.
   */
  lastEditedId?: string | null;

  /**
   * Called when the user drops an item in a new position.
   * The page computes the new orderNo and calls update().
   *
   * @param draggedItem   The item that was dragged
   * @param itemBefore    The item now immediately above it (undefined if first)
   * @param itemAfter     The item now immediately below it (undefined if last)
   * @param newLocalOrder The full array in its new display order
   */
  onReorder?: (
    draggedItem: WorkflowItem,
    itemBefore: WorkflowItem | undefined,
    itemAfter: WorkflowItem | undefined,
    newLocalOrder: WorkflowItem[],
  ) => Promise<void>;

  /**
   * ID of the row whose reorder save is currently in flight.
   * That row's drag handle shows a spinner instead of the grip icon.
   */
  savingOrderId?: string | null;

  /**
   * When false, drag handles are hidden (user lacks update rights).
   * Defaults to true.
   */
  canReorder?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const WorkflowItemTable: React.FC<WorkflowItemTableProps> = ({
  workflowItems,
  loading,
  onEditWorkflowItem,
  onRemoveWorkflowItem,
  removingIds,
  lastEditedId,
  onReorder,
  savingOrderId,
  canReorder = true,
}) => {
  // ------------------------------------------------------------------
  // Drag end — compute neighbours and delegate to the page handler
  // ------------------------------------------------------------------
  const handleDragEnd = useCallback(
    (result: any) => {
      if (!result.destination || !onReorder) return;

      const src = result.source.index;
      const dst = result.destination.index;
      if (src === dst) return;

      const newOrder = Array.from(workflowItems);
      const [dragged] = newOrder.splice(src, 1);
      newOrder.splice(dst, 0, dragged);

      const itemBefore = dst > 0 ? newOrder[dst - 1] : undefined;
      const itemAfter =
        dst < newOrder.length - 1 ? newOrder[dst + 1] : undefined;

      onReorder(dragged, itemBefore, itemAfter, newOrder);
    },
    [workflowItems, onReorder],
  );

  // ------------------------------------------------------------------
  // Loading skeleton
  // ------------------------------------------------------------------
  if (loading && workflowItems.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xl">
        Loading workflow items…
      </Text>
    );
  }

  if (workflowItems.length === 0) {
    return null; // EmptyState is rendered by the page
  }

  // ------------------------------------------------------------------
  // Render a single row — extracted so it can be used both for the
  // normal list and the DnD drag clone (renderClone).
  // ------------------------------------------------------------------
  const renderRow = (item: WorkflowItem, isDragging = false) => {
    const isRemoving = !!item.id && (removingIds?.has(item.id) ?? false);
    const isSavingOrder = savingOrderId === item.id;

    // ------------------------------------------------------------------
    // Condition summary
    // ------------------------------------------------------------------
    const conditionLabel = labelFor(CONDITION_TYPE_LABELS, item.conditionType);

    // Build a human-readable subtitle for the condition:
    //   • ON_FIELD_VALUE_COMPARE → "fieldName  op  value"
    //   • ON_FIELD_EMPTY / NOT_EMPTY → just the field name
    //   • ALWAYS → no subtitle
    let conditionDetail: string | null = null;
    if (item.conditionType === "ON_FIELD_VALUE_COMPARE" && item.formFieldName) {
      const parts = [item.formFieldName];
      if (item.compareOperation) parts.push(item.compareOperation);
      if (item.fieldValue) parts.push(`"${item.fieldValue}"`);
      conditionDetail = parts.join("  ");
    } else if (
      (item.conditionType === "ON_FIELD_EMPTY" ||
        item.conditionType === "ON_FIELD_NOT_EMPTY") &&
      item.formFieldName
    ) {
      conditionDetail = item.formFieldName;
    }

    // ------------------------------------------------------------------
    // Action summary
    // ------------------------------------------------------------------
    const actionLabel = labelFor(ACTION_TYPE_LABELS, item.actionType);

    // Each NOTIFY target gets its own line so nothing is truncated.
    // REJECT shows the full reason text — no truncation needed at this scale.
    const actionDetails: string[] = [];
    if (item.actionType === "NOTIFY") {
      if (item.connector?.name) actionDetails.push(item.connector.name);
      if (item.dataConnector?.name) actionDetails.push(item.dataConnector.name);
      if (item.accountEmail?.email) actionDetails.push(item.accountEmail.email);
    } else if (item.actionType === "REJECT" && item.rejectReason) {
      actionDetails.push(item.rejectReason);
    }

    return (
      <Group gap="sm" wrap="nowrap" align="flex-start" w="100%">
        {/* ---- Drag handle / saving spinner ---- */}
        {canReorder && (
          <Box
            style={{
              cursor: isSavingOrder
                ? "not-allowed"
                : isDragging
                  ? "grabbing"
                  : "grab",
              display: "flex",
              alignItems: "flex-start",
              paddingTop: "2px",
              padding: "4px",
              borderRadius: "4px",
              flexShrink: 0,
            }}
          >
            {isSavingOrder ? (
              <LoadingIndicator size={20} />
            ) : (
              <AppIcon iconName="GripVertical" size={20} color="gray" />
            )}
          </Box>
        )}

        {/* ---- Condition — flex, absorbs available width ---- */}
        <Box style={{ flex: 1, minWidth: 160 }}>
          <Stack gap={2}>
            <Text size="sm" fw={500} lineClamp={1}>
              {conditionLabel}
            </Text>
            {conditionDetail && (
              <Text size="xs" c="dimmed" ff="monospace">
                {conditionDetail}
              </Text>
            )}
          </Stack>
        </Box>

        {/* ---- Action — flex, shares remaining width with Condition ---- */}
        <Box style={{ flex: 1, minWidth: 160 }}>
          <Stack gap={2}>
            <Text size="sm" lineClamp={1}>
              {actionLabel}
            </Text>
            {actionDetails.map((line, i) => (
              <Text key={i} size="xs" c="dimmed">
                {line}
              </Text>
            ))}
          </Stack>
        </Box>

        {/* ---- Stop if met ---- */}
        <Box style={{ width: 90, flexShrink: 0, paddingTop: "2px" }}>
          {item.stopOnConditionMet ? (
            <Badge variant="light" color="orange" size="sm">
              Stop
            </Badge>
          ) : (
            <Badge variant="light" color="gray" size="sm">
              Continue
            </Badge>
          )}
        </Box>

        {/* ---- Status ---- */}
        <Box style={{ width: 100, flexShrink: 0, paddingTop: "2px" }}>
          <StatusBadge status={item.status} />
        </Box>

        {/* ---- Row actions ---- */}
        <Box style={{ flexShrink: 0, paddingTop: "1px" }}>
          <Group gap="xs" wrap="nowrap" justify="flex-end">
            <RecordActions
              context={item}
              onEditClick={onEditWorkflowItem}
              onRemoveClick={onRemoveWorkflowItem}
              removeInProgress={isRemoving}
            />
          </Group>
        </Box>
      </Group>
    );
  };

  // ------------------------------------------------------------------
  // Column header row — styled to match AppTable's thead exactly:
  // background: var(--mantine-primary-color-light)
  // color:      var(--mantine-primary-color-light-color)
  // ------------------------------------------------------------------
  const renderHeader = () => (
    <Group
      gap="sm"
      wrap="nowrap"
      px="md"
      py="md"
      style={{
        background: "var(--mantine-primary-color-light)",
        color: "var(--mantine-primary-color-light-color)",
      }}
    >
      {canReorder && <Box style={{ width: 28, flexShrink: 0 }} />}
      <Box style={{ flex: 1, minWidth: 160 }}>
        <Text fw={500} style={{ color: "inherit" }}>
          Condition
        </Text>
      </Box>
      <Box style={{ flex: 1, minWidth: 160 }}>
        <Text fw={500} style={{ color: "inherit" }}>
          Action
        </Text>
      </Box>
      <Box style={{ width: 90, flexShrink: 0 }}>
        <Text fw={500} style={{ color: "inherit" }}>
          Stop if met
        </Text>
      </Box>
      <Box style={{ width: 100, flexShrink: 0 }}>
        <Text fw={500} style={{ color: "inherit" }}>
          Status
        </Text>
      </Box>
      <Box style={{ width: 80, flexShrink: 0 }} />
    </Group>
  );

  // ------------------------------------------------------------------
  // Main render
  // ------------------------------------------------------------------
  return (
    <Paper withBorder radius="sm" style={{ overflow: "hidden" }}>
      {renderHeader()}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable
          droppableId="workflow-items"
          renderClone={(provided, _snapshot, rubric) => {
            const item = workflowItems[rubric.source.index];
            return (
              <Box
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                px="md"
                py="sm"
                style={{
                  ...provided.draggableProps.style,
                  backgroundColor: "var(--mantine-color-gray-0)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  cursor: "grabbing",
                }}
              >
                {renderRow(item, true)}
              </Box>
            );
          }}
        >
          {(provided) => (
            <Stack gap={0} {...provided.droppableProps} ref={provided.innerRef}>
              {workflowItems.map((item, index) => {
                const isSavingOrder = savingOrderId === item.id;
                const isHighlighted =
                  !!lastEditedId && item.id === lastEditedId;

                return (
                  <Draggable
                    key={item.id ?? `item-${index}`}
                    draggableId={item.id ?? `item-${index}`}
                    index={index}
                    isDragDisabled={!canReorder || isSavingOrder || !onReorder}
                  >
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        px="md"
                        py="sm"
                        style={{
                          ...provided.draggableProps.style,
                          backgroundColor: snapshot.isDragging
                            ? "var(--mantine-color-gray-1)"
                            : isSavingOrder
                              ? "var(--mantine-color-gray-1)"
                              : isHighlighted
                                ? "var(--mantine-color-yellow-0)"
                                : "white",
                          opacity: snapshot.isDragging
                            ? 0.5
                            : isSavingOrder
                              ? 0.7
                              : 1,
                          borderBottom: "1px solid var(--mantine-color-gray-1)",
                          transition: snapshot.isDragging
                            ? "none"
                            : "opacity 0.2s, background-color 0.2s",
                        }}
                      >
                        {renderRow(item, snapshot.isDragging)}
                      </Box>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </Stack>
          )}
        </Droppable>
      </DragDropContext>
    </Paper>
  );
};
